import React from 'react';
import { usePlanStore } from '@/stores/ai/plan-store';
import {
  PlanReviewCard,
  PlanExecutionProgress,
  PlanResearchPanel,
  PlanRoadmapModal,
  PlanAuditResultCard,
  PlanQualityGateDialog,
  PlanPipelineProgress,
  PlanCompletionCard,
  PlanDetailsModal,
  SkillPreviewCard,
  AgentPreviewCard,
  WorkflowPreviewCard,
  DataSchemaPreviewCard,
} from '@/components/ai/plan';
import { KnowledgePreviewCard } from '@/components/ai/plan/KnowledgePreviewCard';
import { usePlanOrchestration } from '@/hooks/ai/usePlanOrchestration';
import type { PropertyMessage } from './types';

export interface PlanModePanelProps {
  planOrchestrator: ReturnType<typeof usePlanOrchestration>;
  roadmapModalOpen: boolean;
  setRoadmapModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  detailsModalOpen: boolean;
  setDetailsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  generateMessageId: () => string;
  setMessages: React.Dispatch<React.SetStateAction<PropertyMessage[]>>;
  persistMessage: (msg: PropertyMessage) => Promise<void>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

export const PlanModePanel: React.FC<PlanModePanelProps> = ({
  planOrchestrator,
  roadmapModalOpen,
  setRoadmapModalOpen,
  detailsModalOpen,
  setDetailsModalOpen,
  isLoading,
  setIsLoading,
  setError,
  generateMessageId,
  setMessages,
  persistMessage,
  inputRef,
}) => {
  // Subscribe to plan state internally — no prop drilling needed
  const planStore = usePlanStore();
  const isFullMode = planStore.pipelineMode === 'full';

  return (
    <>
      {/* Research findings panel (Full Mode, after research completes) */}
      {isFullMode && planStore.researchContext && (planStore.researchContext.findings?.length ?? 0) > 0 && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <PlanResearchPanel
            researchContext={planStore.researchContext}
            isLoading={planStore.isResearching}
            currentTopic={planStore.researchProgress?.currentTopic}
            topicIndex={planStore.researchProgress?.topicIndex}
            totalTopics={planStore.researchProgress?.totalTopics}
            urlsScraped={planStore.researchProgress?.urlsScraped}
          />
        </div>
      )}

      {/* Roadmap modal trigger — CHECKPOINT 1 (Full Mode, after roadmap + audit) */}
      {isFullMode && planStore.roadmap && planStore.pipelineStage === 'reviewing' && !planStore.phases.length && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setRoadmapModalOpen(true)}
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              background: '#FFFFFF',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              color: 'hsl(var(--foreground))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <span>View Roadmap — {(planStore.roadmap.phases ?? []).length} phases</span>
            <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>Click to review</span>
          </button>
        </div>
      )}
      <PlanRoadmapModal
        isOpen={roadmapModalOpen}
        onClose={() => setRoadmapModalOpen(false)}
        roadmap={planStore.roadmap ?? undefined}
        auditResult={planStore.roadmapAudit ?? undefined}
        onContinue={() => { setRoadmapModalOpen(false); planOrchestrator.advanceToPhaseDecomposition(); }}
        onRefineWithChat={() => {
          setRoadmapModalOpen(false);
          planOrchestrator.refineRoadmapWithChat();
          inputRef.current?.focus();
        }}
        onModifyPhase={(phaseId) => {
          setRoadmapModalOpen(false);
          planOrchestrator.modifyRoadmapPhase(phaseId);
          inputRef.current?.focus();
        }}
        onDiscard={() => { setRoadmapModalOpen(false); planStore.reset(); }}
      />

      <PlanDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
      />

      {/* Roadmap audit result (Full Mode, inline after audit) */}
      {isFullMode && planStore.roadmapAudit && planStore.pipelineStage !== 'reviewing' && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <PlanAuditResultCard auditResult={planStore.roadmapAudit} auditType="Roadmap Audit" compact />
        </div>
      )}

      {/* Skill preview card — Skill wizard mode */}
      {planStore.pipelineMode === 'skill' && planStore.pipelineStage === 'reviewing' && planStore.proposedSkill && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <SkillPreviewCard
            skill={planStore.proposedSkill}
            onUpdate={(updated) => planStore.setProposedSkill(updated)}
            onCreateDraft={() => planOrchestrator.confirmSkillCreation(false)}
            onCreateAndActivate={() => planOrchestrator.confirmSkillCreation(true)}
            isCreating={isLoading}
          />
        </div>
      )}

      {/* Agent preview card — Agent wizard mode */}
      {planStore.pipelineMode === 'agent' && planStore.pipelineStage === 'reviewing' && planStore.proposedAgent && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <AgentPreviewCard
            agent={planStore.proposedAgent}
            onUpdate={(updated) => planStore.setProposedAgent(updated)}
            onCreateDraft={() => planOrchestrator.confirmAgentCreation(false)}
            onCreateAndActivate={() => planOrchestrator.confirmAgentCreation(true)}
            isCreating={isLoading}
          />
        </div>
      )}

      {/* Knowledge preview card — Knowledge wizard mode */}
      {planStore.pipelineMode === 'knowledge' && planStore.pipelineStage === 'reviewing' && planStore.proposedKnowledgeBase && (
        <div style={{ padding: '8px 16px 0', flexShrink: 1, minHeight: 0, overflow: 'hidden' }}>
          <KnowledgePreviewCard
            kb={planStore.proposedKnowledgeBase}
            isCreating={isLoading}
            onGoBack={() => {
              planStore.setKnowledgeReady(false);
              planStore.setProposedKnowledgeBase(null);
              planStore.setPipelineStage('conversing');
            }}
            onConfirm={async () => {
              const kb = planStore.proposedKnowledgeBase;
              if (!kb) return;
              setError(null);
              setIsLoading(true);
              try {
                const { createKnowledgeEntry, triggerCompileAll } = await import('@/services/api/knowledgeApi');
                const articles = kb.articles || [];
                for (const article of articles) {
                  await createKnowledgeEntry({
                    text: `# ${article.title}\n\n${article.outline || ''}`,
                    ingestion_path: 'llm_prompted',
                    domain_tags: kb.tags || [],
                    category: 'fact',
                  });
                }
                // Compile entries into articles so they appear in the Knowledge Hub
                try { await triggerCompileAll(); } catch (_) { /* non-critical */ }

                // Exit plan mode cleanly — card will unmount
                const domain = kb.domain || kb.title;
                planStore.reset();

                // Persist guided flow context so "what's next?" works after reset
                sessionStorage.setItem('terramatrix_guided_flow', JSON.stringify({
                  domain,
                  kbTitle: kb.title,
                  lastStep: 'knowledge',
                }));

                // Inject conversational next-steps guidance
                const nextStepsMessage: PropertyMessage = {
                  id: generateMessageId(),
                  role: 'assistant',
                  timestamp: new Date(),
                  metadata: { source: 'agent' },
                  content:
                    `Knowledge base "${kb.title}" created with ${articles.length} article(s) and ${(kb.gaps || []).length} gap(s) to fill later.\n\n` +
                    `Now that I understand your domain, the next step is to **define your data schema** — ` +
                    `the structures your app will use to store and manage information. This will be the foundation for your pages and workflows.`,
                  suggestions: [
                    `Create a data schema for ${domain}`,
                    `Build a page using ${kb.title}`,
                    `Set up a workflow for ${domain}`,
                    `Fill knowledge gaps first`,
                  ],
                };
                setMessages(prev => [...prev, nextStepsMessage]);
                persistMessage(nextStepsMessage);
              } catch (err: unknown) {
                setError(`Failed to create knowledge base: ${err instanceof Error ? err.message : String(err)}`);
              } finally {
                setIsLoading(false);
              }
            }}
          />
        </div>
      )}

      {/* Workflow preview card — Workflow wizard mode */}
      {planStore.pipelineMode === 'workflow' && planStore.pipelineStage === 'reviewing' && planStore.proposedWorkflow && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <WorkflowPreviewCard
            workflow={planStore.proposedWorkflow}
            onUpdate={(updated) => planStore.setProposedWorkflow(updated)}
            onCreateDraft={() => planOrchestrator.confirmWorkflowCreation(false)}
            onCreateAndPublish={() => planOrchestrator.confirmWorkflowCreation(true)}
            isCreating={isLoading}
          />
        </div>
      )}

      {/* Data schema preview card — Data wizard mode */}
      {planStore.pipelineMode === 'data' && planStore.pipelineStage === 'reviewing' && planStore.proposedSchema && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <DataSchemaPreviewCard
            schema={planStore.proposedSchema}
            onUpdate={(updated) => planStore.setProposedSchema(updated)}
            onCreateDraft={() => planOrchestrator.confirmDataSchemaCreation()}
            isCreating={isLoading}
          />
        </div>
      )}

      {/* Plan review card — CHECKPOINT 2 (both modes) */}
      {planStore.pipelineStage === 'reviewing' && planStore.pipelineMode !== 'skill' && planStore.pipelineMode !== 'agent' && planStore.pipelineMode !== 'knowledge' && planStore.pipelineMode !== 'workflow' && planStore.pipelineMode !== 'data' && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          {/* Quick Mode: section-based review */}
          {!isFullMode && planStore.plan && (
            <PlanReviewCard
              plan={planStore.plan}
              mode="quick"
              onExecute={() => planOrchestrator.executePlan()}
              onModify={() => {
                planStore.setPlan(null);
                planStore.setPlanReady(false);
                planStore.setPipelineStage('conversing');
              }}
              onDiscard={() => planStore.reset()}
            />
          )}

          {/* Full Mode: phase-based review with audit indicators */}
          {isFullMode && planStore.phases.length > 0 && (
            <PlanReviewCard
              roadmap={planStore.roadmap ?? undefined}
              phases={planStore.phases}
              phaseAudits={planStore.phaseAudits}
              researchContext={planStore.researchContext ?? undefined}
              mode="full"
              onExecuteAll={() => planOrchestrator.executeAllPhases()}
              onExecutePhase={(phaseId: string) => planOrchestrator.executePhase(phaseId)}
              onModifyPhase={(phaseId: string) => planOrchestrator.modifyPhase(phaseId)}
              onModifyPhaseCriterion={(phaseId: string, criterionName: string) =>
                planOrchestrator.modifyPhase(phaseId, { criterionName })
              }
              onDiscard={() => planStore.reset()}
            />
          )}
        </div>
      )}

      {/* Pipeline progress (Full Mode, during execution) */}
      {isFullMode && planStore.isExecuting && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <PlanPipelineProgress
            pipelineStage={planStore.pipelineStage}
            pipelineProgress={planStore.pipelineProgress}
            phases={planStore.phases}
            currentPhaseId={planStore.currentExecutingPhaseId ?? undefined}
            implementationAudits={planStore.implementationAudits}
            finalAudit={planStore.finalAudit ?? undefined}
            errors={planStore.executionErrors}
            onPause={() => planOrchestrator.pauseExecution()}
            onCancel={() => planStore.abortExecution()}
          />
        </div>
      )}

      {/* Execution progress (Quick Mode, during execution) */}
      {!isFullMode && planStore.isExecuting && planStore.plan && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <PlanExecutionProgress
            plan={planStore.plan}
            currentSectionId={planStore.currentExecutingPhaseId ?? undefined}
            errors={planStore.executionErrors}
            onCancel={() => planStore.abortExecution()}
          />
        </div>
      )}

      {/* Completion card (both modes, after pipeline completes) */}
      {planStore.pipelineStage === 'complete' && planStore.pipelineMode !== 'skill' && (
        <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
          <PlanCompletionCard
            artifactCounts={planStore.artifactCounts ?? {}}
            overallAuditScore={planStore.finalAudit?.overallScore}
            totalLLMCalls={planStore.totalLLMCalls}
            executionTimeMs={planStore.executionTimeMs}
            onStartNewPlan={() => { planStore.reset(); planStore.activate(); }}
            onViewDetails={() => setDetailsModalOpen(true)}
            onClose={() => planStore.reset()}
            // onUndoAll deferred — requires multi-phase undo tracking (UX-4)
          />
        </div>
      )}

      {/* Quality gate dialog (when audit blocks pipeline) */}
      {planStore.activeQualityGate && (
        <PlanQualityGateDialog
          gate={planStore.activeQualityGate}
          onResolve={(action) => planOrchestrator.handleQualityGate(action)}
        />
      )}
    </>
  );
};
